import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useDesignVersionToggle } from '@/hooks'

import { useSnackbar } from '../../helpers/hooks/useSnackbar'
import {
  formatBuildingsForFilter,
  selectActiveBuilding,
  selectBuildingsForFilter,
} from '../../reducers/buildingSelectorReducer'
import { getClientsWithCount, selectClients } from '../../reducers/clientsReducer'
import { selectUserBuilding, selectUserData, selectUserRole } from '../../reducers/userDataReducer'
import Dashboard from '../Dashboard'
import PaginationFooter from '../PaginationFooter/PaginationFooter'

import ClientProfileView from './ClientProfileView/ClientProfileView'
import ClientsNavbar from './ClientsNavbar'
import ClientsTable from './ClientsTable'

const Clients = ({ userData, buildings, getClientsWithCount, clients, selectedBuilding }) => {
  const { t } = useTranslation(['clients-legacy', 'clients'])
  const params = useParams()
  const navigate = useNavigate()

  const { redirect, toggle: handleUIToggle } = useDesignVersionToggle({
    group: 'clients',
    screen: 'Clients',
    to: { path: '/dashboard/clients' },
    version: 'legacy',
  })

  const clientsFilterOptions = () => {
    const options = [
      {
        id: 0,
        text: t('clients-legacy:filterOptions.currentResidents'),
        value: 'current',
      },
      {
        id: 1,
        text: t('clients:list.status.archived'),
        value: 'archived',
      },
    ]

    if (
      (userData.buildingId && userData.buildingId.manualApproval) ||
      userData.user.role === 'Administrator'
    ) {
      options.push({
        id: 2,
        text: t('clients-legacy:filterOptions.pendingApproval'),
        value: 'pending',
      })
    }
    return options
  }

  const findFilterOptionByParams = () => {
    if (params && Object.keys(params).length === 0) {
      return { id: 0, text: t('clients-legacy:filterOptions.currentResidents'), value: 'current' }
    } else if (params) {
      return clientsFilterOptions().find((item) => item.text.toLowerCase().includes(params.filter))
    } else {
      return clientsFilterOptions().find((item) => item.text.toLowerCase())
    }
  }

  const [search, setSearch] = useState('')
  const [clientsFilter, setClientsFilter] = useState(findFilterOptionByParams())
  const [isSiteLoading, setSiteLoading] = useState(false)
  const [showOnPage, setShowOnPage] = useState(25)
  const [pageNumber, setPageNumber] = useState(1)
  const [allClientsCount, setAllClientsCount] = useState(null)
  const [newClientsCounter, setNewClientsCounter] = useState(false)
  const [paginationItemsCount, setPaginationItemsCount] = useState(0)
  const [clientProfileToShow, setClientProfileToShow] = useState(null)

  const { displaySnackbar } = useSnackbar()
  const buildingId = selectedBuilding && selectedBuilding.id

  const refreshClientsTable = () => {
    setSiteLoading(true)
    if (buildingId) {
      getClientsWithCount(1, buildingId, 0, search, showOnPage, clientsFilter.value)
        .then((count) => {
          if (!allClientsCount || newClientsCounter) {
            setAllClientsCount(count)
            setNewClientsCounter(false)
          }
          setPaginationItemsCount(count)
          setPageNumber(1)
        })
        .catch((error) => {
          console.error(error)
        })
        .finally(() => {
          setSiteLoading(false)
        })
    }
  }

  const handleClientProfileToShow = (clientData) => {
    navigate(`/dashboard/clients/${clientData.id}`)
  }

  // TODO: this is bad fix of infinite loader on the page. Consider migrating it to tanstack
  // It may live here for now as workaround because we already have `clients-table.page.tsx`
  useEffect(() => {
    setSiteLoading(false)
  }, [clients])

  if (redirect) return redirect

  return (
    <Dashboard.Container className="ctp-clients">
      <div className="ctp-clients__inner">
        {clientProfileToShow ? (
          <ClientProfileView
            clientData={clientProfileToShow}
            userRole={userData.user.role}
            buildings={buildings}
            closeProfileView={() => setClientProfileToShow(null)}
            showUserStatusPopup={({ status }) =>
              displaySnackbar(t('clients-legacy:snackbar.clientStatus', { status }))
            }
            reduceAllClientsCountByOne={() => setAllClientsCount(allClientsCount - 1)}
            buildingId={buildingId}
          />
        ) : (
          <React.Fragment>
            <ClientsNavbar
              onUIToggle={handleUIToggle}
              buildingId={buildingId}
              buildingName={selectedBuilding?.name}
              search={search}
              isSiteLoading={isSiteLoading}
              userRole={userData.user.role}
              allClientsCount={allClientsCount}
              clientsFilter={clientsFilter}
              refreshClientsTable={refreshClientsTable}
              showUserStatusPopup={({ status }) =>
                displaySnackbar(t('clients-legacy:snackbar.clientStatus', { status }))
              }
              setSiteLoading={setSiteLoading}
              setAllClientsCount={setAllClientsCount}
              setClientsFilter={setClientsFilter}
              setSearch={(e) => {
                setSearch(e)
                setPageNumber(1)
              }}
              clientsFilterOptions={clientsFilterOptions()}
            />
            <div
              className={`ctp-clients-main-table-and-footer-container ${
                isSiteLoading ? 'is-loading' : ''
              }`}
            >
              <ClientsTable
                searchValue={search}
                buildingId={buildingId}
                selectedBuilding={selectedBuilding}
                showOnPage={showOnPage}
                pageNumber={pageNumber}
                clients={clients}
                filter={clientsFilter ? clientsFilter.value : null}
                selectClient={handleClientProfileToShow}
                setPaginationItemsCount={setPaginationItemsCount}
                setSiteLoading={setSiteLoading}
              />
              {clients && clients.length ? (
                <PaginationFooter
                  showOnPage={showOnPage}
                  pageNumber={pageNumber}
                  paginationItemsCount={paginationItemsCount}
                  setSiteLoading={setSiteLoading}
                  setShowOnPage={setShowOnPage}
                  setPageNumber={setPageNumber}
                />
              ) : null}
            </div>
          </React.Fragment>
        )}
      </div>
    </Dashboard.Container>
  )
}

Clients.propTypes = {
  clients: PropTypes.array,
  getClientsWithCount: PropTypes.func,
  userData: PropTypes.object,
  buildings: PropTypes.array,
  selectedBuilding: PropTypes.object,
}

export default connect(
  (state) => {
    const userRole = selectUserRole(state)
    return {
      clients: selectClients(state),
      userData: selectUserData(state),
      selectedBuilding: selectActiveBuilding(state),
      buildings:
        userRole === 'ShiftManager'
          ? formatBuildingsForFilter([selectUserBuilding(state)])
          : selectBuildingsForFilter(state),
    }
  },
  { getClientsWithCount },
)(Clients)
